import { FC, HTMLAttributes } from "react";
import { CalendarDay, Modifiers } from "react-day-picker";

import { NoTradesSession } from "../../../../types/NoTradesSession";
import { Session } from "../../../../types/Session";

import NoTradingDay from "./NoTradingDay";
import TradingDay from "./TradingDay";

import DefaultDay from "./DefaultDay";
import ValidatingCell from "./ValidatingCell";

import { ValidatedWeek } from "../../../../types/ValidatedWeek";
import Text from "../../../../components/Text";
import { getFormattedDateInput } from "../../../../utils/date";
import { isDayMethodRespected } from "../../../../utils/tradingSession";
import defaultDayStyles from "./DefaultDay/styles.module.scss";
import styles from "./styles.module.scss";

const isDateInRange = (
    date: Date,
    startDate: Date,
    endDate: Date
): boolean => {
    const startOfDay = (d: Date) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayTime = startOfDay(date);
    const rangeStart = startOfDay(startDate);
    const rangeEnd = startOfDay(endDate);
    return dayTime >= rangeStart && dayTime <= rangeEnd;
};

export type CustomDayCellProps = {
    startDate: Date;
    endDate: Date;
    hidePerformance: boolean;
    handleDayDetailsOpen: (
        date: string | undefined,
        sessions: (Session | NoTradesSession)[]
    ) => void;
    handleValidWeekFormModalOpen: (
        weekNumber: number,
        year: number,
        weekResult?: string,
        breakdownByTimeslot?: { timeslot: string; total: number }[]
    ) => void;
    sessions: (Session | NoTradesSession)[];
    validatedWeeks: { [key: string]: ValidatedWeek }[];
    day: CalendarDay;
    modifiers: Modifiers;
    weeklyTotal?: number; // total de la semaine (affiché sur la cellule de validation du samedi)
    weekBreakdownByTimeslot?: { timeslot: string; total: number }[];
    weekHasAnySession?: boolean; // true si la semaine a au moins une session (trade ou no trade)
    openRecapModal: (weekNumber: number, weekResult: string, year: number) => void;
    displayedMonth: Date;
    /** Clic sur un jour ouvré vide (lun–ven) dans la plage affichée : choix Trades / No trades (dashboard perso). */
    onEmptyWeekdayClick?: (date: Date) => void;
    /** Avec onEmptyWeekdayClick : si fourni, le clic n’est actif que si true (sinon case non cliquable, sans message). */
    isEmptyCalendarDayAddAllowed?: (date: Date) => boolean;
} & HTMLAttributes<HTMLDivElement>;

const CustomDayCell: FC<CustomDayCellProps> = (props) => {
    const {
        startDate,
        endDate,
        handleDayDetailsOpen,
        handleValidWeekFormModalOpen,
        onEmptyWeekdayClick,
        isEmptyCalendarDayAddAllowed,
        hidePerformance: _omitHidePerformance,
        day,
        modifiers,
        sessions,
        validatedWeeks,
        ...rest
    } = props;
    const isOutside = props.className?.includes("rdp-outside");
    const isOutOfDateRange = !isDateInRange(day.date, startDate, endDate);
    /** Jour hors mois calendrier mais avec sessions (semaine à cheval) : on affiche les perfs et on autorise le détail. */
    const isStraddleWeekWithSessions =
        Boolean(isOutside && sessions?.length);

    const isWeekdayMonFri =
        day.date.getDay() >= 1 && day.date.getDay() <= 5;
    const inDisplayedRange = isDateInRange(day.date, startDate, endDate);
    const canClickEmptyToAdd = Boolean(
        onEmptyWeekdayClick &&
            isWeekdayMonFri &&
            inDisplayedRange &&
            (!sessions || sessions.length === 0) &&
            (!isEmptyCalendarDayAddAllowed ||
                isEmptyCalendarDayAddAllowed(day.date))
    );

    const handleChangeSelectedSession = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (sessions.length === 0) return;
        if (isOutOfDateRange && !isStraddleWeekWithSessions) return;

        const date = getFormattedDateInput(day.date);
        handleDayDetailsOpen(date, sessions);
    };

    // Remove Sundays (0 = Sunday)
    if (day.date.getDay() === 0) {
        return <div {...rest} style={{ display: "none", padding: "6px" }} />;
    }

    // Samedi : toujours la cellule « Semaine » + total (même si le jour est hors mois affiché),
    // pour que les semaines à cheval sur 2 mois montrent le même n° et la même perf sur mars et avril.
    if (day.date.getDay() === 6) {
        return (
            <ValidatingCell
                {...props}
                handleValidWeekFormModalOpen={handleValidWeekFormModalOpen}
                validatedWeeks={validatedWeeks}
                weeklyTotal={props.weeklyTotal}
                weekBreakdownByTimeslot={props.weekBreakdownByTimeslot}
            />
        );
    }

    // Hors mois, sans session : même taille que les autres jours (.cell) + numéro haut-gauche comme DefaultDay
    if (isOutside && (!sessions || sessions.length === 0)) {
        return (
            <div
                {...rest}
                className={`${rest.className ?? ""} ${styles.cell} ${styles.outsideEmptyDayCell} ${
                    canClickEmptyToAdd ? styles.outsideEmptyDayCellClickable : ""
                }`.trim()}
                onClick={
                    canClickEmptyToAdd
                        ? (e) => {
                              e.stopPropagation();
                              onEmptyWeekdayClick!(day.date);
                          }
                        : undefined
                }
                role={canClickEmptyToAdd ? "button" : undefined}
            >
                <div className={defaultDayStyles.customDayCellContent}>
                    <Text
                        className={`${defaultDayStyles.dayNumber} ${styles.outsideEmptyDayNumber}`}
                        variant="body"
                    >
                        {day.date.getDate()}
                    </Text>
                </div>
            </div>
        );
    }

    const outOfRangeClass =
        isOutOfDateRange && !isStraddleWeekWithSessions
            ? styles.outOfRangeCell
            : "";
    const straddleWeekClass = isStraddleWeekWithSessions
        ? styles.straddleWeekDayCell
        : "";

    let cellContent = (
        <DefaultDay
            {...props}
            {...rest}
            className={`${styles.cell} ${outOfRangeClass} ${straddleWeekClass}`}
            onEmptyWeekdayClick={
                canClickEmptyToAdd ? onEmptyWeekdayClick : undefined
            }
        />
    );

    const isMethodRespected = isDayMethodRespected(sessions);

    if (sessions && sessions.length > 0) {
        if (sessions.some((s) => "Trade" in s && s.Trade.length > 0)) {
            cellContent = (
                <TradingDay
                    {...props}
                    onClick={handleChangeSelectedSession}
                    className={`${props.className} ${
                        isMethodRespected
                            ? styles.validMethod
                            : styles.invalidMethod
                    } ${styles.cell} ${outOfRangeClass} ${straddleWeekClass}`}
                />
            );
        } else if (
            sessions.filter((s) => !("Trade" in s)).length === sessions.length
        ) {
            cellContent = (
                <NoTradingDay
                    {...props}
                    onClick={handleChangeSelectedSession}
                    className={`${props.className} ${styles.greyDayCell} ${styles.cell} ${outOfRangeClass} ${straddleWeekClass}`}
                />
            );
        }
    }

    return cellContent;
};

export default CustomDayCell;
